/**
 * @krisspy-file
 * @type page
 * @name "DashboardHome"
 * @title "Dashboard Home"
 * @description "Main dashboard with key metrics and quick actions"
 * @routes ["/dashboard"]
 * @layout "DashboardLayout"
 * @flowName "dashboard"
 * @design "reference"
 */

import { useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import {
  Package,
  DollarSign,
  AlertCircle,
  Calendar,
  Plus,
  ArrowRight,
  TrendingUp,
  Box,
  Wrench
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { getUpcomingBookings, getBookingStats, getWeeklyPerformanceStats, getNext7DaysStats, type BookingWithItems } from '../services/bookings.service';
import { getInventoryStats } from '../services/inventory.service';
import { getIssueStats, type IssueStats } from '../services/issues.service';
import { getMerchantById } from '../services/merchants.service';
import { formatCurrency } from '../lib/currency';
import { useDateFormatter } from '../hooks/useDateFormatter';
import LoadingPlaceholder from '../components/LoadingPlaceholder';

export default function DashboardHome() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { accessControl, merchantId } = useAuth();
  const { formatDate } = useDateFormatter();
  const [loading, setLoading] = useState(true);
  const [currencyCode, setCurrencyCode] = useState<string>('USD');
  const [stats, setStats] = useState({
    activeBookings: 0,
    todayRevenue: 0,
    availableInventory: 0,
    inventoryPercentage: 0,
    totalOutstanding: 0,
    openOrdersCount: 0,
  });
  const [upcomingBookings, setUpcomingBookings] = useState<BookingWithItems[]>([]);
  const [weeklyPerformance, setWeeklyPerformance] = useState({
    thisWeekRevenue: 0,
    thisWeekBookings: 0,
    revenueChange: 0,
    bookingsChange: 0,
  });
  const [next7Days, setNext7Days] = useState({
    tomorrowBookings: 0,
    thisWeekBookings: 0,
    expectedRevenue: 0,
  });
  const [issueStats, setIssueStats] = useState<IssueStats>({
    total: 0,
    reported: 0,
    confirmed: 0,
    repairAssigned: 0,
    overdue: 0,
  });

  useEffect(() => {
    loadDashboardData();
  }, [accessControl, merchantId]);

  async function loadDashboardData() {
    setLoading(true);
    try {
      // Fetch merchant currency
      if (merchantId) {
        const merchantResult = await getMerchantById(merchantId);
        if (merchantResult.data?.currency_code) {
          setCurrencyCode(merchantResult.data.currency_code);
        }
      }

      const today = new Date();
      const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
      const endOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59);

      const [
        todayStatsResult,
        allTimeStatsResult,
        inventoryStatsResult,
        upcomingResult,
        weeklyPerfResult,
        next7DaysResult,
        issueStatsResult,
      ] = await Promise.all([
        // Date-scoped: powers metrics that are genuinely "today only" (revenue today).
        getBookingStats(accessControl, {
          startDate: startOfToday.toISOString(),
          endDate: endOfToday.toISOString(),
        }),
        // Unscoped: powers status counters that should reflect the entire book —
        // active bookings (currently ongoing rentals) and pending payments
        // (all outstanding, not just bookings starting today).
        getBookingStats(accessControl, {}),
        getInventoryStats(accessControl),
        // Only the handful of rows the preview widget renders — asking the
        // database for the next few bookings instead of loading the entire
        // booking history and slicing it here.
        getUpcomingBookings(accessControl, 4),
        getWeeklyPerformanceStats(accessControl),
        getNext7DaysStats(accessControl),
        merchantId ? getIssueStats(merchantId) : Promise.resolve({ data: null, error: null }),
      ]);

      const todayRevenue = todayStatsResult.data?.totalRevenue || 0;
      setStats((prev) => ({
        ...prev,
        activeBookings: allTimeStatsResult.data?.active || 0,
        todayRevenue,
        totalOutstanding: allTimeStatsResult.data?.totalOutstanding || 0,
        openOrdersCount: allTimeStatsResult.data?.openOrdersCount || 0,
      }));

      if (inventoryStatsResult.data) {
        const available = inventoryStatsResult.data.available || 0;
        const total = inventoryStatsResult.data.total || 1;
        const percentage = Math.round((available / total) * 100);
        setStats((prev) => ({
          ...prev,
          availableInventory: available,
          inventoryPercentage: percentage,
        }));
      }

      if (upcomingResult.data) {
        // Already filtered, sorted by start date and limited server-side.
        setUpcomingBookings(upcomingResult.data);
      }

      if (weeklyPerfResult.data) {
        setWeeklyPerformance(weeklyPerfResult.data);
      }

      if (next7DaysResult.data) {
        setNext7Days(next7DaysResult.data);
      }

      if (issueStatsResult.data) {
        setIssueStats(issueStatsResult.data);
      }
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return <LoadingPlaceholder />;
  }

  const metrics = [
    {
      id: 1,
      label: t('dashboard.activeBookings'),
      value: stats.activeBookings.toString(),
      change: t('common.active'),
      changeType: 'neutral',
      icon: Package,
      iconBg: 'bg-blue/10',
      iconColor: 'text-blue',
      badge: { text: t('common.active'), variant: 'badge-info' }
    },
    {
      id: 2,
      label: t('dashboard.todaysRevenue'),
      value: formatCurrency(stats.todayRevenue, currencyCode),
      change: t('time.today'),
      changeType: 'neutral',
      icon: DollarSign,
      iconBg: 'bg-success/10',
      iconColor: 'text-success',
      badge: { text: t('time.today'), variant: 'badge-info' }
    },
    {
      id: 3,
      label: t('dashboard.availableInventory'),
      value: stats.availableInventory.toString(),
      change: `${stats.inventoryPercentage}${t('inventory.percentAvailable')}`,
      changeType: stats.inventoryPercentage >= 70 ? 'positive' : stats.inventoryPercentage >= 40 ? 'neutral' : 'warning',
      icon: Box,
      iconBg: 'bg-warning/10',
      iconColor: 'text-warning',
      badge: { text: `${stats.inventoryPercentage}%`, variant: stats.inventoryPercentage >= 70 ? 'badge-success' : stats.inventoryPercentage >= 40 ? 'badge-info' : 'badge-warning' }
    },
    {
      id: 4,
      label: t('dashboard.outstandingPayments'),
      value: formatCurrency(stats.totalOutstanding, currencyCode),
      change: stats.openOrdersCount > 0
        ? t('dashboard.acrossOpenOrders', { count: stats.openOrdersCount })
        : t('status.allClear'),
      changeType: stats.totalOutstanding > 0 ? 'warning' : 'positive',
      icon: AlertCircle,
      iconBg: 'bg-danger/10',
      iconColor: 'text-danger',
      badge: { text: stats.totalOutstanding > 0 ? t('status.requiresAction') : t('status.clear'), variant: stats.totalOutstanding > 0 ? 'badge-warning' : 'badge-success' }
    }
  ];

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'confirmed':
        return 'badge-success';
      case 'pending':
        return 'badge-warning';
      case 'cancelled':
        return 'badge-danger';
      default:
        return 'badge-info';
    }
  };

  const quickActions = [
    {
      id: 1,
      label: t('actions.newBooking'),
      description: t('actions.createBooking'),
      icon: Plus,
      route: '/bookings/create',
      variant: 'primary'
    },
    {
      id: 2,
      label: t('bookings.viewAllBookings'),
      description: t('actions.manageBookings'),
      icon: Calendar,
      route: '/bookings',
      variant: 'ghost'
    }
  ];

  return (
    <div className="dashboard-container animate-fade-in" data-testid="dashboard-home">
      {/* Welcome Header */}
      <div className="dashboard-header" data-testid="dashboard-home-header">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold text-primary mb-xs">{t('dashboard.title')}</h1>
          <p className="text-xs text-secondary">{t('dashboard.welcomeBack')}</p>
        </div>
        <button
          data-testid="dashboard-home-new-booking-button"
          onClick={() => navigate('/bookings/create')}
          className="btn btn-primary dashboard-header-btn"
        >
          <Plus className="w-4 h-4" />
          {t('bookings.newBooking')}
        </button>
      </div>

      {/* Key Metrics Cards */}
      <div className="dashboard-metrics-grid">
        {metrics.map((metric) => {
          const Icon = metric.icon;
          return (
            <div key={metric.id} className="card">
              <div className="flex items-start justify-between mb-sm">
                <div className={`w-10 h-10 rounded-md ${metric.iconBg} flex items-center justify-center flex-shrink-0`}>
                  <Icon className={`w-5 h-5 ${metric.iconColor}`} />
                </div>
                <span className={`badge text-xs ${metric.badge.variant}`}>
                  {metric.change}
                </span>
              </div>
              <p className="text-secondary text-xs sm:text-sm mb-xs">{metric.label}</p>
              <p className="text-lg sm:text-xl lg:text-2xl font-bold text-primary break-words">{metric.value}</p>
            </div>
          );
        })}
      </div>

      {/* Quick Actions */}
      <div>
        <h2 className="text-base font-bold text-primary mb-sm">{t('dashboard.quickActions')}</h2>
        <div className="dashboard-actions-grid">
          {quickActions.map((action) => {
            const Icon = action.icon;
            return (
              <button
                key={action.id}
                onClick={() => navigate(action.route)}
                className="card-interactive text-left"
              >
                <div className="flex items-start gap-sm mb-sm">
                  <div className="w-10 h-10 rounded-md bg-blue/10 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-5 h-5 text-blue" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-sm text-primary mb-xs">{action.label}</h3>
                    <p className="text-secondary text-xs">{action.description}</p>
                  </div>
                  <ArrowRight className="w-4 h-4 text-secondary flex-shrink-0" />
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Open Issues Widget */}
      {issueStats.total > 0 && (
        <div
          className="card-interactive flex items-center justify-between cursor-pointer"
          onClick={() => navigate('/issues')}
        >
          <div className="flex items-center gap-md">
            <div className="w-10 h-10 rounded-md bg-warning/10 flex items-center justify-center">
              <Wrench className="w-5 h-5 text-warning" />
            </div>
            <div>
              <h3 className="font-semibold text-sm text-primary">{t('dashboard.openIssues')}</h3>
              <p className="text-xs text-secondary">
                {issueStats.reported} {t('dashboard.reported')}, {issueStats.confirmed} {t('dashboard.confirmed')}, {issueStats.repairAssigned} {t('dashboard.inRepair')}
                {issueStats.overdue > 0 && (
                  <span className="text-danger font-medium"> — {issueStats.overdue} {t('dashboard.overdue')}</span>
                )}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-md">
            <span className="text-xl font-bold text-primary">{issueStats.total}</span>
            <ArrowRight className="w-4 h-4 text-secondary" />
          </div>
        </div>
      )}

      {/* Upcoming Bookings */}
      <div>
        <div className="dashboard-section-header">
          <h2 className="text-base font-bold text-primary">{t('dashboard.upcomingBookings')}</h2>
          <button
            onClick={() => navigate('/bookings')}
            className="btn btn-ghost text-xs"
          >
            {t('actions.viewAll')}
            <ArrowRight className="w-3 h-3" />
          </button>
        </div>

        <div className="dashboard-table-card">
          {upcomingBookings.length > 0 ? (
            <div className="dashboard-table-scroll">
              <table className="dashboard-table">
                <thead>
                  <tr>
                    <th>{t('bookings.bookingId')}</th>
                    <th>{t('bookings.customer')}</th>
                    <th>{t('bookings.product')}</th>
                    <th>{t('bookings.start')}</th>
                    <th>{t('bookings.end')}</th>
                    <th>{t('bookings.status')}</th>
                    <th>{t('bookings.amount')}</th>
                  </tr>
                </thead>
                <tbody>
                  {upcomingBookings.map((booking) => {
                    const customerName = `${booking.customer_firstname || ''} ${booking.customer_lastname || ''}`.trim();
                    const productText = booking.items && booking.items.length > 0
                      ? booking.items.length === 1
                        ? (booking.items[0] as any).product_name || 'Unknown'
                        : `${booking.items.length} products`
                      : 'No products';

                    return (
                      <tr
                        key={booking.id}
                        onClick={() => navigate('/bookings')}
                      >
                        <td className="font-medium">{booking.order_id}</td>
                        <td>{customerName}</td>
                        <td className="text-secondary">{productText}</td>
                        <td className="text-secondary">
                          {formatDate(booking.start_date)}
                        </td>
                        <td className="text-secondary">
                          {formatDate(booking.end_date)}
                        </td>
                        <td>
                          <span className={`badge ${getStatusBadge(booking.booking_state)}`}>
                            {booking.booking_state.charAt(0).toUpperCase() + booking.booking_state.slice(1)}
                          </span>
                        </td>
                        <td className="font-medium">
                          {formatCurrency(booking.total_amount, currencyCode)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-2xl">
              <div className="w-16 h-16 rounded-full bg-tertiary mx-auto mb-lg flex items-center justify-center">
                <Calendar className="w-8 h-8 text-secondary" />
              </div>
              <h3 className="text-lg font-semibold text-primary mb-sm">{t('dashboard.noUpcomingBookings')}</h3>
              <p className="text-secondary text-sm mb-xl">
                {t('dashboard.getStartedBooking')}
              </p>
              <button
                onClick={() => navigate('/bookings/create')}
                className="btn btn-primary"
              >
                <Plus className="w-4 h-4" />
                {t('bookings.createBooking')}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Performance Summary */}
      <div className="dashboard-summary-grid">
        <div className="card">
          <div className="flex items-center gap-md mb-lg">
            <div className="w-10 h-10 rounded-md bg-success/10 flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-success" />
            </div>
            <div>
              <h3 className="font-semibold text-primary">{t('dashboard.thisWeekPerformance')}</h3>
              <p className="text-secondary text-sm">{t('common.comparedToLastWeek')}</p>
            </div>
          </div>
          <div className="space-y-md">
            <div className="flex items-center justify-between">
              <span className="text-sm text-secondary">{t('dashboard.totalRevenue')}</span>
              <div className="flex items-center gap-sm">
                <span className="text-sm font-medium text-primary">{formatCurrency(weeklyPerformance.thisWeekRevenue, currencyCode)}</span>
                <span className={`badge ${weeklyPerformance.revenueChange >= 0 ? 'badge-success' : 'badge-danger'}`}>
                  {weeklyPerformance.revenueChange >= 0 ? '+' : ''}{weeklyPerformance.revenueChange}%
                </span>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-secondary">{t('dashboard.newBookings')}</span>
              <div className="flex items-center gap-sm">
                <span className="text-sm font-medium text-primary">{weeklyPerformance.thisWeekBookings}</span>
                <span className={`badge ${weeklyPerformance.bookingsChange >= 0 ? 'badge-success' : 'badge-danger'}`}>
                  {weeklyPerformance.bookingsChange >= 0 ? '+' : ''}{weeklyPerformance.bookingsChange}%
                </span>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-secondary">{t('dashboard.inventoryAvailability')}</span>
              <div className="flex items-center gap-sm">
                <span className="text-sm font-medium text-primary">{stats.inventoryPercentage}%</span>
                <span className={`badge ${stats.inventoryPercentage >= 50 ? 'badge-success' : 'badge-warning'}`}>
                  {stats.inventoryPercentage >= 50 ? t('dashboard.good') : t('dashboard.low')}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center gap-md mb-lg">
            <div className="w-10 h-10 rounded-md bg-blue/10 flex items-center justify-center">
              <Calendar className="w-5 h-5 text-blue" />
            </div>
            <div>
              <h3 className="font-semibold text-primary">{t('dashboard.next7Days')}</h3>
              <p className="text-secondary text-sm">{t('dashboard.upcomingBookingsPreview')}</p>
            </div>
          </div>
          <div className="space-y-md">
            <div className="flex items-center justify-between">
              <span className="text-sm text-secondary">{t('time.tomorrow')}</span>
              <span className="text-sm font-medium text-primary">{next7Days.tomorrowBookings} booking{next7Days.tomorrowBookings !== 1 ? 's' : ''}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-secondary">{t('time.thisWeek')}</span>
              <span className="text-sm font-medium text-primary">{next7Days.thisWeekBookings} booking{next7Days.thisWeekBookings !== 1 ? 's' : ''}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-secondary">{t('dashboard.expectedRevenue')}</span>
              <span className="text-sm font-medium text-success">{formatCurrency(next7Days.expectedRevenue, currencyCode)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
